import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { FilterList } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { VideoObject } from '@nativewaves/nw-cloud-sdk/content'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState, Row } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Foldout } from '@shared/components/Foldout'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  useColumns,
  NewDialog,
  RowActions,
  useAsyncContentCaching,
  FilterPanel,
  SuccessPanelActions,
  SelectionToolbarActions,
  LifecyclePolicyDialog,
} from 'domains/Workspaces/Video/Objects'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { videoObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects'
import { videoObjectWatchRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object'
import { videoObjectQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: videoObjectsOverviewRoute.fullPath })
  const routeParams = videoObjectsOverviewRoute.useParams()
  const { query, groups, excludeGrouped } = videoObjectsOverviewRoute.useSearch()

  const tableInstance = useRef<ReactTableType<VideoObject>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [objectData, setObjectData] = useState<VideoObject | VideoObject[] | undefined>(undefined)

  const { workspaceQuery, videosQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const columns = useColumns('objects')

  const handleSuccess = useCallback(
    (data: VideoObject) => {
      enqueueSnackbar(`${t('domain:Workspace.Event.eventCreated', { name: data.name })}`, {
        description: `${t('domain:Workspace.Event.eventCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} videoobjectId={data.id} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  const handleLifecyclePolicyClick = useCallback((selectedRows: Row<VideoObject>[]) => {
    const objectData = selectedRows.map((row) => row.original)
    setObjectData(objectData)
  }, [])

  useEffect(() => {
    if (videosQuery.isSuccess) {
      tableInstance.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = videosQuery.data?.flat.find((video) => video.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [videosQuery.data?.flat, videosQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Video.searchVideoObject')}
          onSearch={({ search }) => navigate({ search: (prev) => ({ ...prev, query: search }) })}
          defaultValue={query}
        />

        <IconButton size="medium" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
          <FilterList fontSize="small" />
        </IconButton>

        <Foldout open={filterPanelOpen} onClose={() => setFilterPanelOpen(false)} placement="end" hideBackdrop={false}>
          <FilterPanel />
        </Foldout>

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.batchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: videoObjectQueryKeys.list({
                repositoryId: workspaceQuery.data?.contentRepositoryId,
                query,
                groups,
                excludeGrouped,
              }).queryKey,
            })
          }, [
            excludeGrouped,
            groups,
            query,
            queryClient,
            setBatchGetEnabled,
            workspaceQuery.data?.contentRepositoryId,
          ])}
          createAction={useCallback(() => {
            setNewDialogOpen(true)
            tableInstance.current?.resetRowSelection()
          }, [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={48}
        data={videosQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || videosQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={videoObjectsOverviewRoute.fullPath}
            to={videoObjectWatchRoute.to}
            params={{ videoobjectId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        enableInfinity
        infinityProps={pick(videosQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.videoObject', { count: 1 }),
        })}
        RowActions={(props) => <RowActions onClick={(row) => setObjectData(row.original)} {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.videoObject', { count: 2 })}
            discardSelection={() => tableInstance.current?.resetRowSelection()}
            Actions={
              tableInstance.current && (
                <SelectionToolbarActions
                  onLifecyclePolicyClick={() => handleLifecyclePolicyClick(selectedRows)}
                  selectedRows={selectedRows}
                  tableInstance={tableInstance.current}
                />
              )
            }
          />
        )}
        tableOptions={{
          getRowId: (video) => video.id,
        }}
      />
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog onSuccess={handleSuccess} />
      </Dialog>

      <Dialog open={!!objectData} onClose={() => setObjectData(undefined)}>
        {objectData ? (
          <LifecyclePolicyDialog objectData={objectData} onSuccess={() => tableInstance.current?.resetRowSelection()} />
        ) : null}
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
